'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import {
  AuthPageLogo,
  AuthPageWrapper,
} from '@/components/auth/auth-page-wrapper';
import { useLocale } from '@/components/common/locale-provider';
import { toast } from '@/components/shared/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslations } from '@/hooks/use-translations';
import { authClient } from '@/lib/auth/auth-client';

export function ForgotPasswordPageContent() {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [email, setEmail] = useState('');

  const sendResetEmail = async (emailValue: string) => {
    const result = (await authClient.$fetch('/request-password-reset', {
      method: 'POST',
      body: { email: emailValue, redirectTo: '/reset-password' },
    })) as { error?: { message?: string } | string } | undefined;

    if (result?.error) {
      throw new Error('Failed to send');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get('email') as string;

    try {
      await sendResetEmail(emailValue);
      setEmail(emailValue);
      setIsEmailSent(true);
    } catch (_error: unknown) {
      toast({
        type: 'error',
        description: t('auth.forgotPassword.error'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);

    try {
      await sendResetEmail(email);
      toast({
        type: 'success',
        description: t('auth.forgotPassword.resendSuccess'),
      });
    } catch (_error: unknown) {
      toast({
        type: 'error',
        description: t('auth.forgotPassword.error'),
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthPageWrapper>
      <AuthPageLogo title={t('auth.forgotPassword.title')} />

      {isEmailSent ? (
        // Success state
        <div className="flex w-full flex-col items-center gap-6 text-center">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">
              {t('auth.forgotPassword.emailSent')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('auth.forgotPassword.emailSentDescription', { email })}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('auth.forgotPassword.checkSpam')}
            </p>
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {isResending
                ? t('auth.forgotPassword.resending')
                : t('auth.forgotPassword.resendEmail')}
            </button>
          </div>
          <Link
            href="/login"
            className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="size-4" />
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </div>
      ) : (
        // Form state
        <div className="flex w-full flex-col gap-6">
          <p className="text-sm text-muted-foreground text-center">
            {t('auth.forgotPassword.description')}
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                required
                autoFocus
                className="bg-white dark:bg-muted"
              />
            </div>
            <Button type="submit" disabled={isLoading} className="mt-2 w-full">
              {isLoading
                ? t('auth.forgotPassword.sending')
                : t('auth.forgotPassword.sendResetLink')}
            </Button>
          </form>
          <Link
            href="/login"
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </div>
      )}
    </AuthPageWrapper>
  );
}
